import React, { ChangeEvent, KeyboardEvent } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import TextField from '@mui/material/TextField'
import { ReactComponent as SendIcon } from '../../../../../assets/images/arrow-up.svg'
import CircularProgress from '@mui/material/CircularProgress'
import { useCommentInputStyles } from './CommentInput.styles'
import { useIsSnapshotMode } from '../../../../hooks/useIsSnapshotMode'
import { useCanCommentOnRoadmap } from '../../../../hooks/useCanCommentOnRoadmap'

type Props = {
    value: string
    onChange: (value: string) => void
    onSubmit: () => void
    isLoading: boolean
}

export const CommentInput = ({ onSubmit, isLoading, value, onChange }: Props) => {
    const isSnapshotMode = useIsSnapshotMode()
    const canComment = useCanCommentOnRoadmap()

    const styles = useCommentInputStyles()

    const canPostMessage = value.trim().length > 0 && !isLoading

    const handleSetValue = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value)
    }

    const handleAddComment = () => {
        onSubmit()
    }

    const handleAddCommentByPressingEnter = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            if (event.shiftKey) {
                return
            }

            event.preventDefault()

            if (canPostMessage) {
                handleAddComment()
            }
        }
    }

    const isDisabled = isLoading || !canComment || isSnapshotMode

    return (
        <TextField
            disabled={isDisabled}
            multiline
            minRows={1}
            maxRows={4}
            variant="outlined"
            placeholder="Enter your new comment here"
            onChange={handleSetValue}
            onKeyDown={handleAddCommentByPressingEnter}
            value={value}
            fullWidth
            InputProps={{
                classes: {
                    root: styles.root,
                    input: styles.input,
                    notchedOutline: styles.notchedOutline,
                    disabled: styles.notchedOutline,
                },
                endAdornment: (
                    <InputAdornment position="end">
                        <Tooltip enterDelay={600} enterNextDelay={600} title="Post Comment" arrow>
                            <IconButton
                                onClick={handleAddComment}
                                disabled={!canPostMessage}
                                classes={{
                                    root: styles.iconButton,
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <SvgIcon
                                        component={SendIcon}
                                        color={canPostMessage ? 'primary' : 'inherit'}
                                        inheritViewBox
                                        fontSize="small"
                                    />
                                )}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
            }}
        />
    )
}
